import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaMicrophone } from 'react-icons/fa';
import { getTicketAudios, uploadTicketAudio } from '../../services/ticketService';
import { useApp } from '../../context/AppContext';

const MAX_AUDIO_BYTES = 50 * 1024 * 1024;
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm', '.flac'];

const isAudioFile = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith('audio/')) return true;
  const name = String(file.name || '').toLowerCase();
  return AUDIO_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;

  h3,
  h4 {
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  input {
    display: none;
  }
`;

const AudiosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const AudioItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

const AudioPlayer = styled.audio`
  width: 100%;
  max-width: 520px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const AudioMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  max-width: 520px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const AudioName = styled.span`
  font-size: 0.82rem;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OpenLink = styled.a`
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-decoration: underline;
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
`;

const TicketAudiosSection = ({
  ticketId,
  refreshKey = 0,
  allowUpload = true,
  title = 'Áudios',
  compactTitle = false,
  onCountChange,
}) => {
  const { addToast } = useApp();
  const inputRef = useRef(null);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchAudios = useCallback(async () => {
    if (!ticketId) return;
    try {
      setLoading(true);
      const response = await getTicketAudios(ticketId);
      const list = Array.isArray(response) ? response : [];
      setAudios(list);
      onCountChangeRef.current?.(list.length);
    } catch (err) {
      console.error('Erro ao carregar áudios do ticket:', err);
      setAudios([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchAudios();
  }, [fetchAudios, refreshKey]);

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!ticketId || files.length === 0) return;

    const audiosToUpload = files.filter(isAudioFile);
    if (audiosToUpload.length === 0) {
      addToast?.('Selecione um arquivo de áudio.', 'error');
      return;
    }

    const oversized = audiosToUpload.filter((file) => file.size > MAX_AUDIO_BYTES);
    if (oversized.length > 0) {
      addToast?.(
        'Cada áudio deve ter no máximo 50 MB. Remova os arquivos maiores e tente de novo.',
        'error'
      );
    }

    const accepted = audiosToUpload.filter((file) => file.size <= MAX_AUDIO_BYTES);
    if (accepted.length === 0) return;

    try {
      setUploading(true);
      await Promise.all(accepted.map((file) => uploadTicketAudio(ticketId, file)));
      await fetchAudios();
      addToast?.(
        accepted.length === 1 ? 'Áudio enviado.' : `${accepted.length} áudios enviados.`,
        'success'
      );
    } catch (err) {
      console.error('Erro ao enviar áudio:', err);
      addToast?.(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          (err?.response?.status
            ? `Falha ao enviar áudio (${err.response.status})`
            : 'Falha ao enviar o áudio.'),
        'error'
      );
    } finally {
      setUploading(false);
    }
  };

  if (!ticketId) return null;

  const TitleTag = compactTitle ? 'h4' : 'h3';

  return (
    <Section aria-label="Áudios do ticket">
      <SectionHeader>
        <TitleTag>
          <FaMicrophone aria-hidden />
          {title}
          {audios.length > 0 ? ` (${audios.length})` : ''}
        </TitleTag>
        {allowUpload && (
          <UploadButton $disabled={uploading}>
            <input
              ref={inputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
              multiple
              disabled={uploading}
              onChange={handleFilesSelected}
            />
            {uploading ? 'Enviando...' : 'Anexar áudio'}
          </UploadButton>
        )}
      </SectionHeader>

      {loading && audios.length === 0 ? (
        <EmptyHint>Carregando áudios...</EmptyHint>
      ) : audios.length === 0 ? (
        <EmptyHint>Nenhum áudio anexado a este ticket.</EmptyHint>
      ) : (
        <AudiosList>
          {audios.map((audio, index) => (
            <AudioItem key={audio.url || audio.name || index}>
              <AudioPlayer controls preload="metadata" src={audio.url}>
                Seu navegador não suporta a reprodução deste áudio.
              </AudioPlayer>
              <AudioMeta>
                <AudioName>{audio.name || 'Áudio'}</AudioName>
                {audio.url && (
                  <OpenLink href={audio.url} target="_blank" rel="noopener noreferrer">
                    Abrir
                  </OpenLink>
                )}
              </AudioMeta>
            </AudioItem>
          ))}
        </AudiosList>
      )}
    </Section>
  );
};

TicketAudiosSection.propTypes = {
  ticketId: PropTypes.string,
  refreshKey: PropTypes.number,
  allowUpload: PropTypes.bool,
  title: PropTypes.string,
  compactTitle: PropTypes.bool,
  onCountChange: PropTypes.func,
};

export default TicketAudiosSection;
